import { useState, useEffect, useRef, useCallback } from "react";
import { T } from "./theme";

/* ── Thunder — the ThunderCats mascot ──
   A small floating cat that greets the user, then tucks itself
   into a helper bubble. Drag it anywhere on screen; its spot is
   remembered between visits. */

const GREETING = "A couple things here worth checking out!";
const STORAGE_KEY = "karoline_mascot_pos";
const SIZE = 56;
const EDGE = 4;

const CatFace = ({ blink }) => (
  <svg width={40} height={40} viewBox="0 0 64 64" fill="none">
    {/* ears */}
    <path d="M10 22 L20 6 L26 24 Z" fill={T.accent} />
    <path d="M54 22 L44 6 L38 24 Z" fill={T.accent} />
    <path d="M13 21 L20 11 L23.5 22.5 Z" fill="#c9a8fb" />
    <path d="M51 21 L44 11 L40.5 22.5 Z" fill="#c9a8fb" />
    {/* head */}
    <circle cx="32" cy="36" r="22" fill={T.accent} />
    <circle cx="32" cy="37" r="18.5" fill="#ffffff" />
    {/* eyes */}
    <g style={{ transformOrigin: "32px 33px", transform: blink ? "scaleY(0.1)" : "scaleY(1)", transition: "transform 0.12s ease" }}>
      <circle cx="24.5" cy="33" r="2.6" fill="#1A1D2B" />
      <circle cx="39.5" cy="33" r="2.6" fill="#1A1D2B" />
    </g>
    {/* nose + mouth */}
    <path d="M30 40 L34 40 L32 42.5 Z" fill="#e8920a" />
    <path d="M32 42.5 Q32 45 28.5 45.5" stroke="#1A1D2B" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    <path d="M32 42.5 Q32 45 35.5 45.5" stroke="#1A1D2B" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    {/* whiskers */}
    <path d="M14 38 L22 37" stroke="#c9c2f0" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M14 43 L22 41.5" stroke="#c9c2f0" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M50 38 L42 37" stroke="#c9c2f0" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M50 43 L42 41.5" stroke="#c9c2f0" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const clampToViewport = (x, y) => {
  const maxX = window.innerWidth - SIZE - EDGE;
  const maxY = window.innerHeight - SIZE - EDGE;
  return { x: Math.min(Math.max(x, EDGE), Math.max(maxX, EDGE)), y: Math.min(Math.max(y, EDGE), Math.max(maxY, EDGE)) };
};

export default function Mascot({ onShowProviderLifecycle = () => {}, onExplainStationGrouping = () => {} }) {
  const [pos, setPos] = useState(null);
  const [open, setOpen] = useState(false);
  const [blink, setBlink] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dismissTimer = useRef(null);
  const posRef = useRef(null);
  const dragInfo = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false });

  // Restore last saved spot, or default to the bottom-right corner.
  useEffect(() => {
    let initial = { x: window.innerWidth - SIZE - 28, y: window.innerHeight - SIZE - 68 };
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") initial = saved;
    } catch {}
    setPos(clampToViewport(initial.x, initial.y));
  }, []);

  useEffect(() => { posRef.current = pos; }, [pos]);

  // Keep it on-screen if the window resizes.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampToViewport(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Greet on mount, then auto-tuck away.
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open && !dragging) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setOpen(false), 6000);
    }
    return () => clearTimeout(dismissTimer.current);
  }, [open, dragging]);

  // Idle blink, every ~4s.
  useEffect(() => {
    const id = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 160);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const startDrag = useCallback((e) => {
    const point = e.touches ? e.touches[0] : e;
    dragInfo.current = { startX: point.clientX, startY: point.clientY, originX: posRef.current.x, originY: posRef.current.y, moved: false };
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - dragInfo.current.startX;
      const dy = point.clientY - dragInfo.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragInfo.current.moved = true;
      setPos(clampToViewport(dragInfo.current.originX + dx, dragInfo.current.originY + dy));
    };
    const handleUp = () => {
      setDragging(false);
      if (!dragInfo.current.moved) {
        setOpen((o) => !o);
      } else if (posRef.current) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current));
      }
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [dragging]);

  if (!pos) return null;

  const nearRight = pos.x > window.innerWidth / 2;
  const nearBottom = pos.y > window.innerHeight / 2;

  return (
    <div style={{ position: "fixed", left: pos.x, top: pos.y, width: SIZE, height: SIZE, zIndex: 150 }}>
      {/* Speech bubble — flips side depending on which corner the cat is near */}
      <div
        style={{
          position: "absolute",
          [nearBottom ? "bottom" : "top"]: SIZE + 10,
          [nearRight ? "right" : "left"]: 0,
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius,
          padding: "12px 16px", width: 220, boxShadow: T.shadow, fontSize: 13, color: T.text,
          transformOrigin: `${nearBottom ? "bottom" : "top"} ${nearRight ? "right" : "left"}`,
          transform: open ? "scale(1) translateY(0)" : `scale(0.9) translateY(${nearBottom ? 8 : -8}px)`,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "all 0.22s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 11, color: T.accent, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ filter: "drop-shadow(0 0 4px #e8920a)" }}>⚡</span> Thunder
          <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: T.accent, background: T.accentSoft, borderRadius: 999, padding: "2px 6px", textTransform: "uppercase" }}>Info</span>
        </div>
        {GREETING}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShowProviderLifecycle();
            setOpen(false);
          }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            width: "100%", marginTop: 10, padding: "8px 10px",
            background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 8,
            color: T.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
          }}
        >
          Show License-State Diagram
          <span aria-hidden style={{ fontSize: 13 }}>→</span>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onExplainStationGrouping();
            setOpen(false);
          }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            width: "100%", marginTop: 8, padding: "8px 10px",
            background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 8,
            color: T.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
          }}
        >
          Explain Station Grouping
          <span aria-hidden style={{ fontSize: 13 }}>→</span>
        </button>
      </div>

      {/* Floating, draggable mascot button */}
      <button
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        aria-label="Thunder — a couple things here worth checking out! Drag to move."
        title="A couple things here worth checking out! Drag me anywhere."
        style={{
          position: "relative",
          width: SIZE, height: SIZE, borderRadius: "50%", border: `1px solid ${T.border}`,
          background: "linear-gradient(135deg, #f7f6ff, #f0edfe)", cursor: dragging ? "grabbing" : "grab",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: open ? T.accentGlow : "0 4px 14px rgba(124,58,237,0.18)",
          animation: dragging ? "none" : "mascotFloat 3.4s ease-in-out infinite",
          transform: dragging ? "scale(1.08)" : "scale(1)",
          transition: "box-shadow 0.2s, transform 0.15s",
          touchAction: "none", userSelect: "none", padding: 0,
        }}
      >
        <CatFace blink={blink} />
        {/* Always-visible "I have something for you" badge */}
        <span style={{
          position: "absolute", top: -6, right: -6,
          fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4, color: "#fff",
          background: T.accent, borderRadius: 999, padding: "2.5px 6px",
          border: `2px solid ${T.surface}`, textTransform: "uppercase",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)", whiteSpace: "nowrap",
        }}>Info</span>
      </button>

      <style>{`
        @keyframes mascotFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
